import type { CorpusArtifact } from "../../api/types";

/**
 * The known corpus artifact kinds (`corpussvc`'s doc comment: "vision | roadmap_item |
 * decision | governance_rule"), in the display order a Founder reads a project's story:
 * why it exists, what's next, what was decided, what the rules are. `CorpusArtifact.kind`
 * is a plain string on the wire, not a closed union (A-3 — see its doc comment in
 * types.ts), so this list is a RENDERING preference, never a validation gate: an
 * unrecognized kind still groups and renders, just after every known one (see
 * `groupArtifactsByKind` below).
 */
const CORPUS_KIND_ORDER = ["vision", "roadmap_item", "decision", "governance_rule"] as const;

const CORPUS_KIND_LABELS: Record<string, string> = {
  vision: "Vision",
  roadmap_item: "Roadmap",
  decision: "Decisions",
  governance_rule: "Governance",
};

/** A known kind's plural section heading; an unrecognized kind falls back to its own raw
 * name (title-cased) rather than being dropped or mislabeled (A-10). */
export function corpusKindLabel(kind: string): string {
  const known = CORPUS_KIND_LABELS[kind];
  if (known) return known;
  return kind.length > 0 ? kind[0].toUpperCase() + kind.slice(1).replace(/_/g, " ") : kind;
}

export interface CorpusKindGroup {
  kind: string;
  label: string;
  artifacts: CorpusArtifact[];
}

/**
 * Groups a flat artifact page into per-kind sections, ordered `CORPUS_KIND_ORDER` first
 * (any other kind follows, alphabetically, so it's still deterministic) and each group's
 * artifacts sorted by `seq` then `title` (mirrors the natural reading order roadmap items /
 * decisions carry via `seq`; a kind with no ordering, e.g. vision, is `seq: 0` for every
 * item so it falls back to title order).
 */
export function groupArtifactsByKind(artifacts: CorpusArtifact[]): CorpusKindGroup[] {
  const byKind = new Map<string, CorpusArtifact[]>();
  for (const artifact of artifacts) {
    const group = byKind.get(artifact.kind);
    if (group) {
      group.push(artifact);
    } else {
      byKind.set(artifact.kind, [artifact]);
    }
  }

  const knownOrder = new Map(CORPUS_KIND_ORDER.map((kind, index) => [kind as string, index]));
  const kinds = Array.from(byKind.keys()).sort((a, b) => {
    const ai = knownOrder.get(a);
    const bi = knownOrder.get(b);
    if (ai !== undefined && bi !== undefined) return ai - bi;
    if (ai !== undefined) return -1;
    if (bi !== undefined) return 1;
    return a.localeCompare(b);
  });

  return kinds.map((kind) => ({
    kind,
    label: corpusKindLabel(kind),
    artifacts: [...(byKind.get(kind) ?? [])].sort((a, b) => a.seq - b.seq || a.title.localeCompare(b.title)),
  }));
}
